import { useNavigate } from 'react-router-dom';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import TableStyle from '../../ui-component/TableStyle';
import { useState } from 'react';
import { Stack, Button, Container, Typography, Box, Card, Chip, Paper, Grid, alpha, useTheme, Avatar } from '@mui/material';
import * as React from 'react';
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';
import { getApi, patchApi } from 'views/services/api';
import { useEffect } from 'react';
import moment from 'moment';
import { toast } from 'react-toastify';
import {
  AdminPanelSettings as AdminIcon,
  Hotel as HotelIcon,
  BugReport as BugIcon,
  Support as SupportIcon,
  Build as TechnicalIcon,
  Lightbulb as FeatureIcon,
  CheckCircle as ResolvedIcon,
  Schedule as PendingIcon,
  CalendarToday as CalendarIcon,
  TrendingUp as TrendingUpIcon,
  Assignment as ReportIcon,
  Security as SecurityIcon,
  Groups as GroupsIcon
} from '@mui/icons-material';

// ----------------------------------------------------------------------

const AdminComplaint = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [complaintData, setcomplaintData] = useState([]);
  const [statusChanged, setStatusChanged] = useState(false);

  // function for changing the complaint status ---------------------------------

  const handleOpenChangeStatus = async (complaintId) => {
    try {
      const response = await patchApi(`api/complaint/changecomplaintstatus/${complaintId}`);
      if (response.status === 200) {
        toast.success('Reporte resuelto exitosamente');
        setStatusChanged((prevStatusChanged) => !prevStatusChanged);
      } else {
        setStatusChanged(false);
        toast.error('Error al resolver reporte');
      }
    } catch (e) {
      setStatusChanged(false);
      console.log(e);
      toast.error('No se pudo resolver el reporte');
    }
  };

  // function for fetching all the rooms data from the db

  const fetchcomplaintData = async () => {
    try {
      console.log('url hit ==>', `api/complaint/viewallcomplaints`);
      const response = await getApi(`api/complaint/viewallcomplaints`);
      console.log('response print hereeeeee  =====>', response);
      if (response.status === 204) {
        setcomplaintData([]);
      } else {
        setcomplaintData(response?.data?.mergedData);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchcomplaintData();
  }, [statusChanged]);

  // Función para obtener el icono del tipo de reporte
  const getTypeIcon = (type) => {
    const typeMap = {
      bug: BugIcon,
      feature: FeatureIcon,
      support: SupportIcon,
      technical: TechnicalIcon
    };
    const IconComponent = typeMap[type?.toLowerCase()] || BugIcon;
    return <IconComponent sx={{ fontSize: 16, color: theme.palette.primary.main }} />;
  };

  // Función para obtener el color del chip según el tipo
  const getTypeColor = (type) => {
    const colorMap = {
      bug: 'error',
      feature: 'info',
      support: 'warning',
      technical: 'primary'
    };
    return colorMap[type?.toLowerCase()] || 'default';
  };

  // Calcular estadísticas
  const totalComplaints = complaintData?.length || 0;
  const resolvedComplaints = complaintData?.filter((complaint) => complaint.status === true)?.length || 0;
  const pendingComplaints = totalComplaints - resolvedComplaints;
  const uniqueHotels = [...new Set(complaintData?.map((complaint) => complaint.hotelName))].length || 0;

  const columns = [
    {
      field: 'hotelName',
      headerName: 'Hotel',
      flex: 1.2,
      cellClassName: 'name-column--cell name-column--cell--capitalize',
      renderCell: (params) => {
        return (
          <Box onClick={() => navigate(`/dashboard/admincomplaint/view/${params.row._id}`)} sx={{ cursor: 'pointer' }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                  fontSize: '0.75rem',
                  fontWeight: 600
                }}
              >
                {params.value?.charAt(0)?.toUpperCase()}
              </Avatar>
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{
                  '&:hover': {
                    color: theme.palette.primary.main,
                    textDecoration: 'underline'
                  }
                }}
              >
                {params.value}
              </Typography>
            </Stack>
          </Box>
        );
      }
    },
    {
      field: 'type',
      headerName: 'Tipo de Reporte',
      flex: 1,
      renderCell: (params) => (
        <Stack direction="row" alignItems="center" spacing={1}>
          {getTypeIcon(params.value)}
          <Chip
            label={params.value || 'General'}
            color={getTypeColor(params.value)}
            size="small"
            variant="outlined"
            sx={{
              borderRadius: 2,
              fontWeight: 500,
              fontSize: '0.75rem',
              textTransform: 'capitalize'
            }}
          />
        </Stack>
      )
    },
    {
      field: 'description',
      headerName: 'Descripción del Problema',
      flex: 1.8,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100%'
          }}
        >
          {params.value}
        </Typography>
      )
    },
    {
      field: 'createdDate',
      headerName: 'Fecha de Reporte',
      flex: 1.2,
      renderCell: (params) => {
        const isRecent = moment(params.value).isAfter(moment().subtract(24, 'hours'));
        return (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <CalendarIcon sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
            <Box>
              <Typography variant="body2" fontWeight={isRecent ? 600 : 400} color={isRecent ? 'primary' : 'text.primary'}>
                {moment(params.value).format('DD/MM/YYYY')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {moment(params.value).format('HH:mm')}
              </Typography>
            </Box>
          </Stack>
        );
      }
    },
    {
      field: 'status',
      headerName: 'Estado',
      flex: 1,
      cellClassName: `name-column--cell--capitalize`,
      renderCell: (params) => {
        const isResolved = params.value === true;
        return (
          <Chip
            label={isResolved ? 'Resuelto' : 'Pendiente'}
            color={isResolved ? 'success' : 'warning'}
            variant="filled"
            size="small"
            icon={isResolved ? <ResolvedIcon sx={{ fontSize: 16 }} /> : <PendingIcon sx={{ fontSize: 16 }} />}
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              fontSize: '0.75rem',
              minWidth: 100
            }}
          />
        );
      }
    },
    {
      field: 'action',
      headerName: 'Acción',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        const isResolved = params.row.status === true;
        return (
          <Button
            variant={isResolved ? 'outlined' : 'contained'}
            color={isResolved ? 'success' : 'primary'}
            size="small"
            startIcon={<ChangeCircleIcon />}
            onClick={() => handleOpenChangeStatus(params.row._id)}
            disabled={isResolved}
            sx={{
              fontSize: '0.75rem',
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              px: 2
            }}
          >
            {isResolved ? 'Resuelto' : 'Marcar Resuelto'}
          </Button>
        );
      }
    }
  ];

  return (
    <>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Header Section */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
            color: 'white',
            borderRadius: 3
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h4" fontWeight={700} gutterBottom>
                Administración de Soporte Global
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                Panel centralizado para gestionar reportes de soporte de todos los hoteles
              </Typography>
            </Box>
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <SecurityIcon sx={{ fontSize: 60, opacity: 0.3 }} />
            </Box>
          </Stack>
        </Paper>

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={3}>
            <Card
              sx={{
                p: 2.5,
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: theme.shadows[8]
                }
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <ReportIcon sx={{ color: theme.palette.primary.main, fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={600} color="primary">
                    {totalComplaints}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total de Reportes
                  </Typography>
                </Box>
              </Stack>
            </Card>
          </Grid>

          <Grid item xs={12} sm={3}>
            <Card
              sx={{
                p: 2.5,
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: theme.shadows[8]
                }
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.success.main, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <ResolvedIcon sx={{ color: theme.palette.success.main, fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={600} color="success.main">
                    {resolvedComplaints}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Casos Resueltos
                  </Typography>
                </Box>
              </Stack>
            </Card>
          </Grid>

          <Grid item xs={12} sm={3}>
            <Card
              sx={{
                p: 2.5,
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.warning.main, 0.1)}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: theme.shadows[8]
                }
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.warning.main, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <PendingIcon sx={{ color: theme.palette.warning.main, fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={600} color="warning.main">
                    {pendingComplaints}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Casos Pendientes
                  </Typography>
                </Box>
              </Stack>
            </Card>
          </Grid>

          <Grid item xs={12} sm={3}>
            <Card
              sx={{
                p: 2.5,
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: theme.shadows[8]
                }
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.info.main, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <HotelIcon sx={{ color: theme.palette.info.main, fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={600} color="info.main">
                    {uniqueHotels}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Hoteles Activos
                  </Typography>
                </Box>
              </Stack>
            </Card>
          </Grid>
        </Grid>

        {/* Controls Section */}
        <Card sx={{ p: 3, mb: 3, borderRadius: 3, border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems="center" spacing={2} justifyContent="space-between">
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Centro de Administración de Soporte
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Gestiona y resuelve reportes de soporte técnico de todos los hoteles del sistema
              </Typography>
            </Box>

            <Stack direction="row" alignItems="center" spacing={2}>
              <Chip
                label={`${pendingComplaints} pendientes`}
                color={pendingComplaints > 0 ? 'warning' : 'success'}
                variant="filled"
                sx={{ fontWeight: 600 }}
              />
            </Stack>
          </Stack>
        </Card>

        {/* Data Table */}
        <TableStyle>
          <Card
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              '& .MuiDataGrid-root': {
                border: 'none',
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: alpha(theme.palette.secondary.main, 0.05),
                  borderBottom: `2px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
                  '& .MuiDataGrid-columnHeader': {
                    fontWeight: 600,
                    fontSize: '0.875rem'
                  }
                },
                '& .MuiDataGrid-row': {
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.secondary.main, 0.02)
                  }
                },
                '& .MuiDataGrid-cell': {
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.05)}`
                }
              }
            }}
          >
            <Box sx={{ p: 3, pb: 1 }}>
              <Typography variant="h6" fontWeight={600} color="secondary.main">
                Reportes de Soporte Global ({totalComplaints})
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Haz clic en un hotel para ver los detalles completos del reporte
              </Typography>
            </Box>

            <Box sx={{ height: 600, width: '100%' }}>
              <DataGrid
                rows={complaintData}
                columns={columns}
                checkboxSelection
                getRowId={(row) => row?._id}
                slots={{ toolbar: GridToolbar }}
                slotProps={{
                  toolbar: {
                    showQuickFilter: true,
                    quickFilterProps: {
                      debounceMs: 500,
                      placeholder: 'Buscar reportes por hotel, tipo o descripción...'
                    }
                  }
                }}
                sx={{
                  '& .MuiDataGrid-toolbarContainer': {
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    p: 2
                  }
                }}
              />
            </Box>
          </Card>
        </TableStyle>
      </Container>
    </>
  );
};

export default AdminComplaint;
